import { formatSeconds } from '@qim/shared'
import type {
  PlanAction,
  PlanCounts,
  RunLogSummary,
  RunSummaryHeadline,
  TimelinePhase,
} from '@qim/shared'
import type { JSX } from 'react'

/**
 * What a run's log says it did, above the log (IM-35, IM-165).
 *
 * The log of a real apply is 62,000 lines, and two thirds of the plan in it is
 * CRD schemas nobody reads. What somebody opening a run wants first is three
 * answers, in this order:
 *
 * 1. **Will anything be destroyed?** So destroys and replaces come first, in
 *    full, before any count of what is merely created — and the ones that hold
 *    data are flagged in words. A replace of the database hidden at row 300 of
 *    a 443-row plan is the failure this card exists to prevent.
 * 2. **What else changes**, by component, with the addresses one click away.
 * 3. **Where the time went**, for an apply or a destroy: the installer's phases
 *    and the slowest resources.
 *
 * Nothing here is parsed in the browser from a stored run: the API made the
 * summary once, from the whole log. A running apply's summary *is* made here,
 * from the pages the live panel is already fetching — see `run.tsx`.
 *
 * State is a word and a shape, with colour third, like every status in the
 * console: this card is exactly the thing that gets screenshotted into an
 * approval thread.
 */

/** Each action's word and glyph. The word is what a reader scans for. */
const ACTION: Readonly<Record<PlanAction, { readonly word: string; readonly glyph: string }>> = {
  create: { word: 'create', glyph: '+' },
  update: { word: 'change', glyph: '~' },
  replace: { word: 'replace', glyph: '±' },
  destroy: { word: 'destroy', glyph: '✕' },
  read: { word: 'read', glyph: '→' },
}

/*
 * One literal class per branch rather than an interpolated one, so the
 * stylesheet test can see every class this file asks for.
 */
const ChangeWord = ({ action }: { readonly action: PlanAction }): JSX.Element => {
  const { word, glyph } = ACTION[action]
  const inner = (
    <>
      <span aria-hidden="true">{glyph}</span> {word}
    </>
  )
  switch (action) {
    case 'destroy':
      return <span className="change change-destroy">{inner}</span>
    case 'replace':
      return <span className="change change-replace">{inner}</span>
    case 'create':
    case 'update':
    case 'read':
      return <span className="change">{inner}</span>
  }
}

/** "443 to create · 0 to change · 0 to replace · 0 to destroy", reads only when there are any. */
export const planCountsText = (counts: PlanCounts): string =>
  [
    `${String(counts.create)} to create`,
    `${String(counts.update)} to change`,
    `${String(counts.replace)} to replace`,
    `${String(counts.destroy)} to destroy`,
    ...(counts.read > 0 ? [`${String(counts.read)} to read`] : []),
  ].join(' · ')

/** Only the actions that happen, for a component's line: "3 to create, 1 to read". */
const nonZeroCountsText = (counts: PlanCounts): string =>
  (['create', 'update', 'replace', 'destroy', 'read'] as const)
    .filter((a) => counts[a] > 0)
    .map((a) => `${String(counts[a])} to ${ACTION[a].word}`)
    .join(', ')

const DOING: Readonly<Record<PlanAction, string>> = {
  create: 'creating',
  update: 'changing',
  replace: 'replacing',
  destroy: 'destroying',
  read: 'reading',
}

const PlanCountsLine = ({ counts }: { readonly counts: PlanCounts }): JSX.Element => (
  <p className="summary-counts">
    <span>{counts.create} to create</span> · <span>{counts.update} to change</span> ·{' '}
    {/* The two that take something away are set apart when they are not zero,
      in weight and with their glyph, so a count of one is not lost in a row. */}
    {counts.replace > 0 ? (
      <strong className="summary-destructive-count">± {counts.replace} to replace</strong>
    ) : (
      <span>0 to replace</span>
    )}{' '}
    ·{' '}
    {counts.destroy > 0 ? (
      <strong className="summary-destructive-count">✕ {counts.destroy} to destroy</strong>
    ) : (
      <span>0 to destroy</span>
    )}
    {counts.read > 0 ? <span className="muted"> · {counts.read} to read</span> : null}
  </p>
)

const phaseDuration = (phase: TimelinePhase, live: boolean): string => {
  if (phase.seconds !== undefined) return formatSeconds(phase.seconds)
  if (phase.open) return live ? 'running' : 'did not finish'
  // No timestamp at one edge. Said, never guessed.
  return 'unknown'
}

const clockOf = (at: string | undefined): string => (at === undefined ? '' : at.slice(11, 19))

export const RunSummaryCard = ({
  summary,
  live = false,
}: {
  readonly summary: RunLogSummary
  /** The run is still going: the summary covers what has been read so far. */
  readonly live?: boolean
}): JSX.Element => {
  if (summary.parsed === 'unparseable') {
    return (
      <div className="run-summary">
        <p className="muted">
          Nothing to summarise: {summary.reason ?? 'no plan, apply or destroy in this log.'}
        </p>
      </div>
    )
  }

  const { plan, timeline } = summary
  const running = timeline.inProgress

  return (
    <div className="run-summary" aria-label="Run summary">
      {live && running.length > 0 ? (
        <p className="summary-now" role="status">
          {running.slice(0, 3).map((r, i) => (
            <span key={r.address}>
              {i === 0 ? 'Now ' : ', '}
              {`${DOING[r.action]} `}
              <code>{r.address}</code>
              {r.elapsedSeconds === undefined ? '' : ` (${formatSeconds(r.elapsedSeconds)})`}
            </span>
          ))}
          {running.length + timeline.moreInProgress > 3
            ? `, and ${String(running.length + timeline.moreInProgress - 3)} more`
            : ''}
        </p>
      ) : null}

      {summary.outcome === undefined ? null : (
        <p className="summary-outcome">
          <strong>
            {summary.outcome.command === 'destroy' ? 'Destroy complete' : 'Apply complete'}:
          </strong>{' '}
          {summary.outcome.command === 'destroy'
            ? `${String(summary.outcome.destroyed)} destroyed`
            : `${String(summary.outcome.added)} added, ${String(summary.outcome.changed)} changed, ${String(summary.outcome.destroyed)} destroyed`}
          {timeline.totalSeconds === undefined
            ? ''
            : ` · ${formatSeconds(timeline.totalSeconds)} in all`}
        </p>
      )}

      {summary.parsed === 'partial' && !live ? (
        <p className="muted">Partial summary: {summary.reason}</p>
      ) : null}

      {plan === undefined ? null : (
        <>
          <h4>{summary.shape === 'plan' ? 'What this plan would change' : 'What it planned'}</h4>
          <PlanCountsLine counts={plan.counts} />
          {/* Both counts, never a pick between them: a wrong number here is
            what an approver would act on. */}
          {plan.agreement === 'disagrees' ? (
            <p className="failure" role="alert">
              {plan.disagreement}
            </p>
          ) : null}
          {plan.agreement === 'no-headers' && plan.planLine !== undefined ? (
            <p className="muted">
              Terraform counted {plan.planLine.add} to add, {plan.planLine.change} to change and{' '}
              {plan.planLine.destroy} to destroy, but printed no resource list to read.
            </p>
          ) : null}

          {plan.destructive.length === 0 ? null : (
            <div className="summary-destructive">
              <h5>
                Destroyed or replaced
                {plan.sensitiveCount > 0
                  ? ` — ${String(plan.sensitiveCount)} ${plan.sensitiveCount === 1 ? 'holds' : 'hold'} data or the cluster`
                  : ''}
              </h5>
              <ul>
                {plan.destructive.map((change) => (
                  <li key={change.address}>
                    <ChangeWord action={change.action} /> <code>{change.address}</code>
                    {change.sensitive === undefined ? null : (
                      <p className="sensitive-flag">
                        <span aria-hidden="true">⚠</span> {change.sensitive}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {plan.moreDestructive > 0 ? (
                <p className="muted">
                  and {plan.moreDestructive} more — every destroy is in the full log.
                </p>
              ) : null}
            </div>
          )}

          {plan.components.length === 0 ? null : (
            <ul className="summary-components">
              {plan.components.map((component) => (
                <li key={component.component}>
                  {/* Collapsed: the addresses are in hand already, so opening
                    one costs no read — but 443 of them open at once is the
                    log again. */}
                  <details>
                    <summary>
                      <code>{component.component}</code> — {component.total}{' '}
                      <span className="muted">({nonZeroCountsText(component.counts)})</span>
                    </summary>
                    <ul>
                      {component.addresses.map((a) => (
                        <li key={a.address}>
                          <ChangeWord action={a.action} /> <code>{a.address}</code>
                        </li>
                      ))}
                    </ul>
                    {component.more > 0 ? <p className="muted">and {component.more} more</p> : null}
                  </details>
                </li>
              ))}
            </ul>
          )}
          {plan.moreComponents > 0 ? (
            <p className="muted">and {plan.moreComponents} more components</p>
          ) : null}
          {plan.passes > 1 ? (
            <p className="muted">
              Terraform planned {plan.passes} times in this run; these are the last plan&rsquo;s
              counts.
            </p>
          ) : null}
        </>
      )}

      {summary.shape === 'plan' || timeline.phases.length === 0 ? null : (
        <div className="summary-timeline">
          <h4>Timeline</h4>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Phase</th>
                  <th scope="col">Started</th>
                  <th scope="col">Took</th>
                </tr>
              </thead>
              <tbody>
                {timeline.phases.map((phase) => (
                  <tr key={`${phase.id}-${String(phase.attempt)}`}>
                    <td>
                      {phase.label}
                      {phase.attempt > 1 ? ` · attempt ${String(phase.attempt)}` : ''}
                    </td>
                    <td>
                      <time dateTime={phase.startedAt}>{clockOf(phase.startedAt)}</time>
                    </td>
                    <td>{phaseDuration(phase, live)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {timeline.slowest.length === 0 ? null : (
            <>
              <h5>Slowest resources</h5>
              <ol className="summary-slowest">
                {timeline.slowest.map((r) => (
                  <li key={r.address}>
                    <code>{r.address}</code> — {formatSeconds(r.seconds)}
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}

      {!live && running.length > 0 ? (
        <p className="muted">
          Still in progress when the log ended: {running.map((r) => r.address).join(', ')}
          {timeline.moreInProgress > 0 ? `, and ${String(timeline.moreInProgress)} more` : ''}
        </p>
      ) : null}

      <p className="muted">
        {summary.warnings.terraform} Terraform{' '}
        {summary.warnings.terraform === 1 ? 'warning' : 'warnings'} · {summary.warnings.installer}{' '}
        installer {summary.warnings.installer === 1 ? 'warning' : 'warnings'}
        {summary.failures === undefined || summary.failures.count === 0
          ? ''
          : ` · ${String(summary.failures.count)} distinct ${summary.failures.count === 1 ? 'error' : 'errors'}${
              summary.failures.distinct[0]?.resource === undefined
                ? ''
                : `, on ${summary.failures.distinct
                    .map((f) => f.resource ?? '')
                    .filter((r) => r !== '')
                    .join(', ')}`
            } — the message is in the log`}
      </p>
    </div>
  )
}

/**
 * The one line a history row shows, from the run record — no read at all.
 *
 * Absent until somebody opens the finished run, which is when the summary is
 * made. A row with nothing is the honest answer for a run nobody has looked at.
 */
export const RunSummaryHeadlineText = ({
  headline,
}: {
  readonly headline: RunSummaryHeadline
}): JSX.Element | null => {
  if (headline.parsed === 'unparseable') return null
  if (headline.outcome !== undefined) {
    const o = headline.outcome
    return (
      <p className="muted summary-headline">
        {o.command === 'destroy'
          ? `${String(o.destroyed)} destroyed`
          : `${String(o.added)} added · ${String(o.changed)} changed · ${String(o.destroyed)} destroyed`}
        {headline.seconds === undefined ? '' : ` in ${formatSeconds(headline.seconds)}`}
      </p>
    )
  }
  if (headline.plan === undefined) return null
  return (
    <p className="muted summary-headline">
      {planCountsText(headline.plan)}
      {headline.sensitive === undefined ? null : (
        <strong className="summary-destructive-count">
          {' '}
          · <span aria-hidden="true">⚠</span> {headline.sensitive} holding data
        </strong>
      )}
    </p>
  )
}
